import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { generateDefaultUsers } from '../../../slices/organizations.slice';
import { defaultContentRatingList } from '../../../utils/contentRatings';
import { parseOrgCode } from '../../../utils/organization';
import BadgeSelect from '../../../components/BadgeSelect';
import RoleBadge from '../../../components/RoleBadge';

// Mirror of the backend's DEFAULT_USER_SPECS (services/default-users.js) so the
// preview matches exactly what will be created.
const USER_PREVIEW = [
    { key: 'avit',  role: 'site-admin', pin: '5555', defaultRestrictions: [] },
    { key: 'owner', role: 'site-admin', pin: '1111', defaultRestrictions: [] },
    { key: 'vip',   role: 'site-user',  pin: '0000', defaultRestrictions: [] },
    { key: 'guest', role: 'site-guest', pin: '0000', defaultRestrictions: [...defaultContentRatingList] },
];

const initialRestrictions = () =>
    Object.fromEntries(USER_PREVIEW.map(u => [u.key, [...u.defaultRestrictions]]));

const GenerateDefaultUsersModal = ({ isOpen, toggle, organization }) => {
    const dispatch = useDispatch();
    const [restrictions, setRestrictions] = useState(initialRestrictions);
    const [generating, setGenerating] = useState(false);

    // Validate the name format up front and derive the 5-digit code.
    const code = useMemo(() => parseOrgCode(organization?.name), [organization?.name]);

    // Reset the picker every time the modal opens so it reflects the defaults.
    useEffect(() => {
        if (isOpen) setRestrictions(initialRestrictions());
    }, [isOpen]);

    // Ratings are a cumulative ceiling: picking a value allows everything up to
    // and including it; clicking the current ceiling clears it (full access).
    const handleRating = (key, value) => {
        const current = restrictions[key];
        const isCeiling = current.length > 0 && Math.max(...current) === value;
        const next = isCeiling
            ? []
            : defaultContentRatingList.slice(0, defaultContentRatingList.indexOf(value) + 1);
        setRestrictions({ ...restrictions, [key]: next });
    };

    const handleGenerate = async () => {
        setGenerating(true);
        const promise = dispatch(generateDefaultUsers({ orgId: organization._id, restrictions })).unwrap();
        toast.promise(promise, {
            pending: 'Generating default users...',
            success: 'Default users generated',
            error: { render: ({ data }) => (typeof data === 'string' ? data : 'Error generating default users') },
        });
        try {
            await promise;
            toggle();
        } catch {
            // surfaced by the toast above
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Generate default users</ModalHeader>
            <ModalBody>
                {!code ? (
                    <Alert color="warning" className="mb-0">
                        <span className="fas fa-triangle-exclamation me-2"/>
                        The organization name <strong>{organization?.name}</strong> does not match the
                        required <code>BAC-XX-00000</code> format, so default users cannot be generated.
                        Rename the organization first.
                    </Alert>
                ) : (
                    <>
                        <p>
                            These accounts will be created for <strong>{organization.name}</strong> (code{' '}
                            <strong>{code}</strong>). Existing accounts have their password, pin, role and
                            content ratings reset. Adjust the allowed content ratings per user below if needed.
                        </p>
                        {USER_PREVIEW.map(({ key, role, pin }) => (
                            <div key={key} className="rounded p-3 mb-3" style={{ border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                    <div>
                                        <strong>{key}@{code}</strong>
                                        <span className="text-white-50 ms-2 small">
                                            password: <code className="text-primary">{key}{code}</code>
                                            {' · '}pin: <code className="text-primary">{pin}</code>
                                        </span>
                                    </div>
                                    <RoleBadge role={role}/>
                                </div>

                                {role === 'site-admin' ? (
                                    <div className="small text-white-50">
                                        <span className="fas fa-circle-info me-1"/>
                                        Admins bypass content ratings.
                                    </div>
                                ) : (
                                    <>
                                        <div className="small text-white-50 mb-1">Allowed content ratings</div>
                                        <BadgeSelect
                                            defaultList={defaultContentRatingList}
                                            values={restrictions[key]}
                                            onChange={(value) => handleRating(key, value)}
                                            multiple
                                            append="+"
                                        />
                                        {restrictions[key].length === 0 && (
                                            <div className="small text-white-50">No restriction — full access.</div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" outline onClick={toggle}>Cancel</Button>
                <Button color="primary" onClick={handleGenerate} disabled={!code || generating}>
                    {generating
                        ? (<><span className="fas fa-spinner fa-spin me-2"/>Generating...</>)
                        : 'Generate'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default GenerateDefaultUsersModal;
